<template>
  <div class="team-lineup-page">
    <header class="team-lineup-header">
      <v-btn icon class="back-btn" @click="goBack">
        <v-icon>arrow_back</v-icon>
      </v-btn>
      <div class="team-lineup-title-wrap">
        <img
          :src="(leagueData && leagueData.Logo) || defaultLeagueLogo"
          :alt="leagueName"
          class="team-lineup-logo"
          @error="onLeagueLogoError"
        />
        <div class="team-lineup-titles">
          <h1 class="team-lineup-title">{{ schoolDisplay || "Team" }}</h1>
          <p class="team-lineup-subtitle">
            {{ leagueName }} — top wrestler per weight class
          </p>
        </div>
      </div>
    </header>

    <div class="team-lineup-content">
      <div v-if="loading" class="skeleton-lineup">
        <skeleton-loader type="list-item" />
        <skeleton-loader type="list-item" />
        <skeleton-loader type="list-item" />
      </div>
      <div v-else-if="missingParams" class="empty-state-wrap">
        <p class="empty-state">
          Missing league or school. Open this page from team rankings.
        </p>
      </div>
      <div v-else class="lineup-list">
        <div
          v-for="row in rows"
          :key="row.wc.id || String(row.wc.Weight)"
          class="lineup-block"
        >
          <div class="lineup-weight-head">{{ row.wc.Weight }}</div>
          <div v-if="!row.wrestler" class="lineup-empty">
            No wrestler from this school in this weight class.
          </div>
          <div
            v-else
            class="lineup-wrestler-row"
            @click="goToWrestler(row.wrestler.id)"
          >
            <img
              v-if="
                authenticated &&
                row.wrestler.data.Image &&
                row.wrestler.data.Image !== ''
              "
              :src="row.wrestler.data.Image"
              class="lineup-avatar"
              alt=""
            />
            <img
              v-else
              :src="require('../assets/default-profile.png')"
              class="lineup-avatar"
              alt=""
            />
            <div class="lineup-info">
              <span class="lineup-name">
                {{ row.wrestler.data.FirstName }}
                {{ row.wrestler.data.LastName }}
              </span>
              <div
                class="lineup-skill-bar skill-bar skill-bar--linear"
                :aria-label="
                  'Skill rating ' +
                  formatSkillLevel(
                    row.wrestler.data && row.wrestler.data.Skill != null
                      ? row.wrestler.data.Skill
                      : 0
                  )
                "
              >
                <div class="skill-bar-linear-track">
                  <div
                    class="skill-bar-linear-fill"
                    :style="{
                      width: lineupSkillBarPercent(row.wrestler) + '%',
                    }"
                  ></div>
                </div>
                <span class="skill-bar-label">{{
                  formatSkillLevel(
                    row.wrestler.data && row.wrestler.data.Skill != null
                      ? row.wrestler.data.Skill
                      : 0
                  )
                }}</span>
              </div>
              <span class="lineup-meta">
                ({{ row.wrestler.data.Wins || 0 }}–{{
                  row.wrestler.data.Losses || 0
                }})
                <template v-if="row.wrestler.data.Grade">
                  · {{ row.wrestler.data.Grade }} Grade</template
                >
              </span>
            </div>
            <v-icon class="lineup-chevron" small>chevron_right</v-icon>
          </div>
        </div>
      </div>
    </div>
    <FloatingNav />
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";
import FloatingNav from "../components/FloatingNav.vue";
import { fetchTopAthleteForSchoolAtWeight } from "../utils/fetchTopAthleteSchoolAtWeight";
import {
  seasonFilterState,
  filterAthleteRowsBySeason,
  isAllSeasons,
} from "../utils/seasonFilter";
import { formatSkillLevel } from "../utils/formatSkillDisplay";
import {
  fetchLeagueSkillMinMax,
  skillBarLinearFillPercent as linearSkillBarFillPercent,
  computeEffectiveSkillBarRange,
  SKILL_BAR_MIN,
} from "../utils/skillBarLinear";
import { authState } from "../utils/authState";

export default {
  name: "LeagueTeamLineup",
  components: {
    FloatingNav,
    "skeleton-loader": () => import("../components/SkeletonLoader"),
  },
  data() {
    return {
      defaultLeagueLogo: require("../assets/logo.svg"),
      leagueData: null,
      weightclasses: [],
      rows: [],
      loading: true,
      leagueSkillMin: null,
      leagueSkillMax: null,
    };
  },
  computed: {
    leagueId() {
      return this.$route.params.leagueId || "";
    },
    schoolQuery() {
      const q = this.$route.query.school;
      const s = Array.isArray(q) ? q[0] : q;
      if (s == null) return "";
      return String(s).trim();
    },
    leagueName() {
      return (this.leagueData && this.leagueData.Name) || "";
    },
    schoolDisplay() {
      return this.schoolQuery;
    },
    missingParams() {
      return !this.leagueId || !this.schoolQuery;
    },
    lineupSkillNumbers() {
      return (this.rows || [])
        .map((r) => r.wrestler)
        .filter(Boolean)
        .map((w) => {
          const s = w.data && w.data.Skill;
          return typeof s === "number" ? s : Number(s);
        })
        .filter((n) => Number.isFinite(n));
    },
    lineupBarRange() {
      return computeEffectiveSkillBarRange(
        this.leagueSkillMin,
        this.leagueSkillMax,
        ...this.lineupSkillNumbers
      );
    },
  },
  watch: {
    "$route.params.leagueId"() {
      this.loadAll();
    },
    "$route.query.school"() {
      this.loadAll();
    },
  },
  mounted() {
    this.loadAll();
    this._seasonUnwatch = this.$watch(
      () => seasonFilterState.selected,
      () => {
        this.loadAll();
      }
    );
  },
  beforeDestroy() {
    if (this._seasonUnwatch) this._seasonUnwatch();
  },
  methods: {
    formatSkillLevel,
    onLeagueLogoError(e) {
      if (e && e.target && this.defaultLeagueLogo)
        e.target.src = this.defaultLeagueLogo;
    },
    goBack() {
      if (this.leagueId) {
        this.$router.push({
          name: "league-rankings",
          params: { leagueId: this.leagueId },
        });
      } else {
        this.$router.go(-1);
      }
    },
    goToWrestler(id) {
      this.$router.push("/wrestler/" + id);
    },
    lineupSkillBarPercent(wrestler) {
      const s = wrestler.data && wrestler.data.Skill;
      const skill = typeof s === "number" ? s : Number(s);
      const val = Number.isFinite(skill) ? skill : SKILL_BAR_MIN;
      const { low, high } = this.lineupBarRange;
      return linearSkillBarFillPercent(val, high, low);
    },
    async loadLeagueSkillBounds() {
      const league = this.leagueName;
      if (!league) {
        this.leagueSkillMin = null;
        this.leagueSkillMax = null;
        return;
      }
      const db = getFirestore();
      try {
        const r = await fetchLeagueSkillMinMax(db, league);
        this.leagueSkillMin = r.min;
        this.leagueSkillMax = r.max;
      } catch (_e) {
        this.leagueSkillMin = null;
        this.leagueSkillMax = null;
      }
    },
    async loadAll() {
      if (this.missingParams) {
        this.leagueData = null;
        this.weightclasses = [];
        this.rows = [];
        this.loading = false;
        return;
      }
      this.loading = true;
      const db = getFirestore();
      const leagueId = this.leagueId;
      const school = this.schoolQuery;
      try {
        const leagueRef = doc(db, "leagues", leagueId);
        const leagueSnap = await getDoc(leagueRef);
        if (!leagueSnap.exists()) {
          this.leagueData = null;
          this.weightclasses = [];
          this.rows = [];
          return;
        }
        this.leagueData = leagueSnap.data();
        const wcRef = collection(db, "leagues", leagueId, "weightclasses");
        const q = query(wcRef, orderBy("Sort"), orderBy("Weight"));
        const wcSnap = await getDocs(q);
        const wcs = [];
        wcSnap.forEach((d) => wcs.push({ id: d.id, ...d.data() }));

        const league = this.leagueName;
        if (!league || !wcs.length) {
          this.weightclasses = wcs;
          this.rows = wcs.map((wc) => ({ wc, wrestler: null }));
          await this.loadLeagueSkillBounds();
          return;
        }

        const rawRows = await Promise.all(
          wcs.map(async (wc) => {
            const wrestler = await fetchTopAthleteForSchoolAtWeight(
              db,
              league,
              wc,
              school
            );
            return { wc, wrestler };
          })
        );

        let rows = rawRows;
        if (!isAllSeasons(seasonFilterState.selected)) {
          const withW = rawRows.filter((r) => r.wrestler);
          const filtered = await filterAthleteRowsBySeason(
            db,
            withW.map((r) => r.wrestler),
            seasonFilterState.selected
          );
          const allowed = new Set(filtered.map((r) => r.id));
          rows = rawRows.map((r) => ({
            wc: r.wc,
            wrestler:
              r.wrestler && allowed.has(r.wrestler.id) ? r.wrestler : null,
          }));
        }

        this.weightclasses = wcs;
        this.rows = rows;
        await this.loadLeagueSkillBounds();
      } catch (_e) {
        this.leagueData = null;
        this.weightclasses = [];
        this.rows = [];
        this.leagueSkillMin = null;
        this.leagueSkillMax = null;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.team-lineup-page {
  min-height: 100%;
  background: #f5f5f7;
  padding-bottom: 2rem;
}

.team-lineup-header {
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.back-btn {
  flex-shrink: 0;
}

.team-lineup-title-wrap {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.team-lineup-logo {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}

.team-lineup-titles {
  min-width: 0;
}

.team-lineup-title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.team-lineup-subtitle {
  margin: 0.2rem 0 0;
  font-size: 0.85rem;
  color: #6b7280;
}

.team-lineup-content {
  max-width: 640px;
  margin: 0 auto;
  padding: 0 1rem;
}

.skeleton-lineup {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.lineup-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.lineup-block {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.lineup-weight-head {
  font-weight: 700;
  font-size: 1rem;
  color: #1f2937;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
}

.lineup-empty {
  padding: 16px;
  color: #6b7280;
  font-size: 0.9rem;
}

.lineup-wrestler-row {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  gap: 12px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.lineup-wrestler-row:hover {
  background: #f5f5f5;
}

.lineup-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.lineup-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.lineup-name {
  font-weight: 600;
  font-size: 0.95rem;
  color: #1f2937;
}

.lineup-meta {
  font-size: 0.8rem;
  color: #6b7280;
}

.lineup-skill-bar.skill-bar--linear {
  width: 100%;
  max-width: none;
  min-width: 0;
  align-self: stretch;
  height: 14px;
  position: relative;
}

.lineup-skill-bar .skill-bar-linear-track {
  width: 100%;
  height: 14px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
}

.lineup-skill-bar .skill-bar-linear-fill {
  height: 100%;
  border-radius: 999px;
  min-width: 0;
  background: linear-gradient(
    90deg,
    #22c55e 0%,
    #a3e635 22%,
    #facc15 48%,
    #f97316 74%,
    #b91c1c 100%
  );
  transition: width 0.25s ease;
}

.lineup-skill-bar .skill-bar-label {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.65rem;
  font-weight: 600;
  color: #111827;
  pointer-events: none;
}

.lineup-chevron {
  color: #d1d5db;
  flex-shrink: 0;
}

.empty-state-wrap {
  text-align: center;
  padding: 2rem 1rem;
}

.empty-state {
  margin: 0;
  color: #6b7280;
}

.theme--dark .team-lineup-page {
  background: #121212;
}
.theme--dark .team-lineup-header {
  background: #1e1e1e;
}
.theme--dark .team-lineup-title {
  color: #e5e7eb;
}
.theme--dark .team-lineup-subtitle {
  color: #9ca3af;
}
.theme--dark .lineup-block {
  background: #1e1e1e;
}
.theme--dark .lineup-weight-head {
  color: #e5e7eb;
  background: #252525;
  border-bottom-color: rgba(255, 255, 255, 0.08);
}
.theme--dark .lineup-empty {
  color: #9ca3af;
}
.theme--dark .lineup-wrestler-row:hover {
  background: #2d2d2d;
}
.theme--dark .lineup-name {
  color: #e5e7eb;
}
.theme--dark .lineup-meta {
  color: #9ca3af;
}
.theme--dark .lineup-skill-bar .skill-bar-linear-track {
  background: #374151;
}
.theme--dark .lineup-skill-bar .skill-bar-label {
  color: #f3f4f6;
}
.theme--dark .empty-state {
  color: #9ca3af;
}
</style>
